import { atomFamily, selectorFamily, DefaultValue, useRecoilValue } from 'recoil';
import type { RecoilState } from 'recoil';
import type { WindowLevelDisplayState } from './types';
import {
  broadcastChannelSynchronizerEffect,
  localStoragePersisterEffect,
} from 'utils/recoilEffects';
import type { ViewportTypeKeys } from 'config/constants';
import equals from 'ramda/es/equals';
import { activeViewportState, activeSeriesState } from 'config/recoilState';
import { useGuideViewportConfigurationSelector } from './state';
import type { WithApolloContext } from 'modules/Apollo/types';

/**
 * Dedicated atom for window/level values, separated from ViewportDisplayConfiguration
 * to allow fine-grained subscriptions. Components that only read window/level
 * won't re-render when pan/zoom changes, and vice versa.
 */
type WindowLevelDisplayStateWrapper = {
  created: Nullable<number>;
  updated: Nullable<number>;
  data: Nullable<WindowLevelDisplayState>;
};

export const DEFAULT_WINDOW_LEVEL_STATE: WindowLevelDisplayState = {
  window: undefined,
  level: undefined,
  window3d: undefined,
  level3d: undefined,
  windowLayered: undefined,
  levelLayered: undefined,
};

export const windowLevelDisplayState = atomFamily<
  WindowLevelDisplayStateWrapper,
  { seriesSmid: string; viewType: ViewportTypeKeys; viewportId: string }
>({
  key: 'windowLevelDisplay',
  default: {
    created: Date.now(),
    updated: null,
    data: null,
  },
  effects: [broadcastChannelSynchronizerEffect(), localStoragePersisterEffect()],
});

export const windowLevelDisplaySelector: (
  param:
    | {
        seriesSmid: Nullable<string>;
        viewType: Nullable<ViewportTypeKeys>;
        viewportId: Nullable<string>;
      }
    | undefined
) => RecoilState<Nullable<WindowLevelDisplayState>> = selectorFamily({
  key: 'windowLevelDisplaySelector',
  get:
    (payload) =>
    ({ get }) => {
      const { seriesSmid, viewType, viewportId } = payload || {};
      if (seriesSmid == null || viewType == null || viewportId == null) return null;
      return get(windowLevelDisplayState({ seriesSmid, viewType, viewportId })).data ?? null;
    },
  set:
    (payload) =>
    ({ set }, newData: Nullable<DefaultValue | WindowLevelDisplayState>) => {
      const { seriesSmid, viewType, viewportId } = payload || {};
      if (seriesSmid == null || viewType == null || viewportId == null) return;

      if (newData instanceof DefaultValue) {
        set(windowLevelDisplayState({ seriesSmid, viewType, viewportId }), (state) => ({
          created: state.created,
          updated: Date.now(),
          data: null,
        }));
      } else {
        set(windowLevelDisplayState({ seriesSmid, viewType, viewportId }), (state) => {
          if (!(state instanceof DefaultValue) && equals(state.data, newData)) {
            return state;
          }

          return {
            created: state.created,
            updated: Date.now(),
            data: newData,
          };
        });
      }
    },
  cachePolicy_UNSTABLE: {
    eviction: 'most-recent',
  },
});

/**
 * Hook to retrieve the window/level selector for the guide (active) viewport.
 */
export function useGuideWindowLevelSelector({
  context,
}: WithApolloContext): RecoilState<Nullable<WindowLevelDisplayState>> {
  const viewportId = useRecoilValue(activeViewportState);
  const viewportConfiguration = useRecoilValue(useGuideViewportConfigurationSelector({ context }));
  const seriesSmid = useRecoilValue(activeSeriesState);

  return windowLevelDisplaySelector({
    seriesSmid: seriesSmid || viewportConfiguration?.series?.smid,
    viewType: viewportConfiguration?.viewType,
    viewportId,
  });
}
